import _, { parseInt } from 'lodash';
import { adminClient as apolloClient } from '@/store/apollo';

import ConversationsPaginatedGql from '@/extensions/communication/store/graphql/queries/ConversationsPaginated.gql';
import ConversationGql from '@/extensions/communication/store/graphql/queries/Conversation.gql';
import SendMessageGql from '@/extensions/communication/store/graphql/mutations/SendMessage.gql';
import CreateNoteGql from '@/extensions/communication/store/graphql/mutations/CreateNote.gql';
import DeleteNoteGql from '@/extensions/communication/store/graphql/mutations/DeleteNote.gql';
import StartConversationGql from '@/extensions/communication/store/graphql/mutations/StartConversation.gql';
import MarkConversationReadGql from '@/extensions/communication/store/graphql/mutations/MarkConversationRead.gql';
import SetConversationSubjectGql from '@/extensions/communication/store/graphql/mutations/SetConversationSubject.gql';
import MarkMessageReadGql from '@/extensions/communication/store/graphql/mutations/MarkMessageRead.gql';
import CloseConversationGql from '@/extensions/communication/store/graphql/mutations/CloseConversation.gql';
import DeleteConversationGql from '@/extensions/communication/store/graphql/mutations/DeleteConversation.gql';
import MarkConversationSpamGql from '@/extensions/communication/store/graphql/mutations/MarkConversationSpam.gql';
import BulkConversationActionGql from '@/extensions/communication/store/graphql/mutations/BulkConversationAction.gql';
import SetConversationIndicatorsGql from '@/extensions/communication/store/graphql/mutations/SetConversationIndicators.gql';
import ResendMessageGql from '@/extensions/communication/store/graphql/mutations/ResendMessage.gql';
import SyncInboundGql from '@/extensions/communication/store/graphql/mutations/SyncInbound.gql';
import LinkConversationToMemberGql from '@/extensions/communication/store/graphql/mutations/LinkConversationToMember.gql';
import LinkConversationToPurchaseGql from '@/extensions/communication/store/graphql/mutations/LinkConversationToPurchase.gql';
import LinkConversationToTicketGql from '@/extensions/communication/store/graphql/mutations/LinkConversationToTicket.gql';
import CreateTicketFromConversationGql from '@/extensions/communication/store/graphql/mutations/CreateTicketFromConversation.gql';
import MembersSearchGql from '@/extensions/communication/store/graphql/queries/MembersSearch.gql';
import PurchasesForLinkGql from '@/extensions/communication/store/graphql/queries/PurchasesForLink.gql';
import WhatsAppTemplatesGql from '@/extensions/communication/store/graphql/queries/WhatsAppTemplates.gql';

export default {
    fetchConversationsPaginated: data =>
        new Promise((resolve, reject) => {
            const filters = [];
            _.each(data.filters, entry => filters.push(entry));

            apolloClient
                .query({
                    query: ConversationsPaginatedGql,
                    fetchPolicy: 'network-only',
                    variables: {
                        currentPage: _.get(data, 'pageInfo.currentPage', 1),
                        perPage: _.get(data, 'pageInfo.perPage', 25),
                        order: _.get(data, 'variables.order', 'last_message_at'),
                        direction: _.get(data, 'variables.direction', 'desc'),
                        link: _.get(data, 'link.route', '/conversations/'),
                        filters
                    }
                })
                .then(({ data }) => resolve(data.EX_COMM_ConversationsPaginated))
                .catch(err => reject(err));
        }),

    // `ticketId` narrows the thread to that ticket's own messages; the inbox
    // omits it and gets the whole conversation.
    fetchConversation: (id, ticketId = null) =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: ConversationGql,
                    fetchPolicy: 'network-only',
                    variables: { id: parseInt(id), ticket_id: ticketId ? parseInt(ticketId) : null }
                })
                .then(({ data }) => resolve(data.EX_COMM_Conversation))
                .catch(err => reject(err));
        }),

    // Embedded feeds (member tab / purchase tab) ride the SAME paginated query as
    // the inbox, just pre-filtered — one backend query, no per-relation clones.
    fetchMemberConversations: memberID =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: ConversationsPaginatedGql,
                    fetchPolicy: 'network-only',
                    variables: {
                        currentPage: 1,
                        perPage: 100,
                        order: 'last_message_at',
                        direction: 'desc',
                        link: '/conversations/',
                        filters: [{ key: 'member', match: String(parseInt(memberID)) }]
                    }
                })
                .then(({ data }) => resolve(data.EX_COMM_ConversationsPaginated.items))
                .catch(err => reject(err));
        }),

    fetchPurchaseConversations: purchaseID =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: ConversationsPaginatedGql,
                    fetchPolicy: 'network-only',
                    variables: {
                        currentPage: 1,
                        perPage: 100,
                        order: 'last_message_at',
                        direction: 'desc',
                        link: '/conversations/',
                        filters: [{ key: 'purchase', match: String(parseInt(purchaseID)) }]
                    }
                })
                .then(({ data }) => resolve(data.EX_COMM_ConversationsPaginated.items))
                .catch(err => reject(err));
        }),

    sendMessage: variables =>
        new Promise((resolve, reject) => {
            const normalized = { ...variables };
            if (normalized.conversation_id != null) normalized.conversation_id = parseInt(normalized.conversation_id, 10);

            apolloClient
                .mutate({ mutation: SendMessageGql, variables: normalized })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_COMM_SendMessage);
                })
                .catch(err => reject(err));
        }),

    startConversation: variables =>
        new Promise((resolve, reject) => {
            // The mutation declares Int for these — coerce so a string id (e.g.
            // cms_id "557" from the store) doesn't trip GraphQL's Int validation.
            const normalized = { ...variables };
            if (normalized.cms_id != null) normalized.cms_id = parseInt(normalized.cms_id, 10);
            if (normalized.account_id != null) normalized.account_id = parseInt(normalized.account_id, 10);
            if (normalized.point_of_sale_id != null) normalized.point_of_sale_id = parseInt(normalized.point_of_sale_id, 10);
            if (normalized.purchase_id != null) normalized.purchase_id = parseInt(normalized.purchase_id, 10);
            if (normalized.ticket_id != null) normalized.ticket_id = parseInt(normalized.ticket_id, 10);
            if (normalized.member && normalized.member.id != null) {
                normalized.member = { ...normalized.member, id: parseInt(normalized.member.id, 10) };
            }

            apolloClient
                .mutate({ mutation: StartConversationGql, variables: normalized })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_COMM_StartConversation);
                })
                .catch(err => reject(err));
        }),
    createNote: (conversationID, body) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: CreateNoteGql,
                    variables: { conversation_id: parseInt(conversationID), body },
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_COMM_CreateNote);
                })
                .catch(err => reject(err));
        }),

    deleteNote: id =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({ mutation: DeleteNoteGql, variables: { id: parseInt(id) } })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_COMM_DeleteNote);
                })
                .catch(err => reject(err));
        }),

    markRead: (id, read = true) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({ mutation: MarkConversationReadGql, variables: { id: parseInt(id), read } })
                .then(({ data }) => resolve(data.EX_COMM_MarkConversationRead))
                .catch(err => reject(err));
        }),

    setSubject: (id, subject) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({ mutation: SetConversationSubjectGql, variables: { id: parseInt(id), subject: subject || null } })
                .then(({ data }) => resolve(data.EX_COMM_SetConversationSubject))
                .catch(err => reject(err));
        }),

    markMessageRead: (id, read = true) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({ mutation: MarkMessageReadGql, variables: { id: parseInt(id), read } })
                .then(({ data }) => resolve(data.EX_COMM_MarkMessageRead))
                .catch(err => reject(err));
        }),

    close: (id, reopen = false) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({ mutation: CloseConversationGql, variables: { id: parseInt(id), reopen } })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_COMM_CloseConversation);
                })
                .catch(err => reject(err));
        }),

    deleteConversation: id =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({ mutation: DeleteConversationGql, variables: { id: parseInt(id) } })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_COMM_DeleteConversation);
                })
                .catch(err => reject(err));
        }),

    markSpam: (id, unmark = false) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({ mutation: MarkConversationSpamGql, variables: { id: parseInt(id), unmark } })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_COMM_MarkConversationSpam);
                })
                .catch(err => reject(err));
        }),

    bulkAction: (ids, action) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: BulkConversationActionGql,
                    variables: { ids: ids.map(id => parseInt(id)), action }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_COMM_BulkConversationAction);
                })
                .catch(err => reject(err));
        }),

    setIndicators: (ids, indicatorIds, mode) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: SetConversationIndicatorsGql,
                    variables: {
                        ids: ids.map(id => parseInt(id)),
                        indicator_ids: indicatorIds.map(id => parseInt(id)),
                        mode
                    }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_COMM_SetConversationIndicators || []);
                })
                .catch(err => reject(err));
        }),

    syncInbound: (cmsID, pointOfSaleID = null) =>
        new Promise((resolve, reject) => {
            const variables = { cms_id: parseInt(cmsID) };
            if (pointOfSaleID) variables.point_of_sale_id = parseInt(pointOfSaleID);

            apolloClient
                .mutate({ mutation: SyncInboundGql, variables })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_COMM_SyncInbound);
                })
                .catch(err => reject(err));
        }),

    resendMessage: messageId =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({ mutation: ResendMessageGql, variables: { message_id: parseInt(messageId) } })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_COMM_ResendMessage);
                })
                .catch(err => reject(err));
        }),

    linkToMember: (conversationID, memberID) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: LinkConversationToMemberGql,
                    variables: { conversation_id: parseInt(conversationID), member: memberID ? { id: parseInt(memberID) } : null }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_COMM_LinkConversationToMember);
                })
                .catch(err => reject(err));
        }),

    searchMembers: (cmsID, term) =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: MembersSearchGql,
                    fetchPolicy: 'network-only',
                    variables: { cms: parseInt(cmsID), query: term || '', take: 20 }
                })
                .then(({ data }) => resolve(data.EX_RSTRCT_Members))
                .catch(err => reject(err));
        }),

    // Approved WhatsApp templates for the conversation's WhatsApp account — used to
    // message a customer outside the 24h session window.
    fetchWhatsAppTemplates: accountID =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: WhatsAppTemplatesGql,
                    fetchPolicy: 'network-only',
                    variables: { account_id: parseInt(accountID, 10) }
                })
                .then(({ data }) => resolve(data.EX_COMM_WhatsAppTemplates || []))
                .catch(err => reject(err));
        }),

    // Linking a conversation to a ticket does not copy anything: the
    // conversation IS the thread, so it moves out of the inbox onto the ticket.
    // Make a ticket OF this thread: it becomes the ticket's thread, nothing is copied.
    createTicket: (conversationID, { subject = null, priority = null } = {}) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: CreateTicketFromConversationGql,
                    variables: { conversation_id: parseInt(conversationID), subject, priority },
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_COMM_CreateTicketFromConversation);
                })
                .catch(err => reject(err));
        }),

    linkToTicket: (conversationID, ticketID) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: LinkConversationToTicketGql,
                    variables: { conversation_id: parseInt(conversationID), ticket_id: ticketID ? parseInt(ticketID) : null }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_COMM_LinkConversationToTicket);
                })
                .catch(err => reject(err));
        }),

    linkToPurchase: (conversationID, purchaseID) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: LinkConversationToPurchaseGql,
                    variables: { conversation_id: parseInt(conversationID), purchase: purchaseID ? { id: parseInt(purchaseID) } : null }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_COMM_LinkConversationToPurchase);
                })
                .catch(err => reject(err));
        }),

    // Orders to choose from in the "link to order" picker — a member's orders when
    // the conversation is already linked to a customer, else the shop's recent ones.
    fetchPurchasesForLink: ({ member = null, shop = null, search = null, take = 50 } = {}) =>
        new Promise((resolve, reject) => {
            const variables = { take };
            if (member) variables.member = parseInt(member);
            if (shop) variables.shop = parseInt(shop);
            if (search) variables.search = search;

            apolloClient
                .query({
                    query: PurchasesForLinkGql,
                    fetchPolicy: 'network-only',
                    variables
                })
                .then(({ data }) => resolve(data.CO_Purchases))
                .catch(err => reject(err));
        })
};
